import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';  
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext();

export const AuthProvider = React.memo(function AuthProvider({ children }) {  
  const [user, setUser] = useState(null);  
  const [loading, setLoading] = useState(true);  
  const [authError, setAuthError] = useState(null);

  useEffect(() => {  
    // Check for active session when the component mounts  
    const checkSession = async () => {  
      try {  
        const { data, error } = await supabase.auth.getSession();  
          
        if (error) {  
          console.error("Session error:", error);  
          setAuthError(error.message);  
        } else {  
          setUser(data?.session?.user || null);  
        }  
      } catch (err) {  
        console.error("Session check error:", err);  
        setAuthError(err.message);  
      } finally {  
        setLoading(false);  
      }  
    };  
      
    checkSession();

    // Subscribe to auth changes  
    const { data: { subscription } } = supabase.auth.onAuthStateChange(  
      (event, session) => {  
        setUser(session?.user || null);  
        setLoading(false);  
      }  
    );

    // Cleanup subscription on unmount  
    return () => {  
      if (subscription && typeof subscription.unsubscribe === 'function') {  
        subscription.unsubscribe();  
      }  
    };  
  }, []);

  // Sign up with email and password  
  const signUp = useCallback(async (email, password, options = {}) => {
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: options.data || {}  // This allows passing metadata
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Signup error:", error);
      setAuthError(error.message);
      return { data: null, error };
    }
  }, []);

  // Sign in with email and password  
  const signIn = useCallback(async (email, password) => {  
    setAuthError(null);  
    try {  
      // Clear any previous user's localStorage data
      localStorage.removeItem('globalOverhead');
      localStorage.removeItem('lastSelectedOffer');
      
      const { data, error } = await supabase.auth.signInWithPassword({  
        email,  
        password,  
      });  
        
      if (error) throw error;  
      return { data, error: null };  
    } catch (error) {  
      console.error("Signin error:", error);  
      setAuthError(error.message);  
      return { data: null, error };  
    }  
  }, []);

  // Sign out  
  const signOut = useCallback(async () => {  
    setAuthError(null);  
    try {  
      // Clear user-specific localStorage items BEFORE signing out
      localStorage.removeItem('globalOverhead');
      localStorage.removeItem('lastSelectedOffer');
      localStorage.removeItem('desiredTab');
      localStorage.removeItem('currentOfferName');
      localStorage.removeItem('profitAnalysisActiveTab');
      localStorage.removeItem('tempOfferName');
      localStorage.removeItem('resetToFirstTab');
      
      // Clear any offer backups
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('offer_backup_') || 
            key.startsWith('dashboard_offer_backup_') ||
            key.startsWith('form_state_backup_')) {
          localStorage.removeItem(key);
        }
      });
      
      // Now do the actual sign out
      const { error } = await supabase.auth.signOut();  
        
      if (error) throw error;  
    } catch (error) {  
      console.error("Signout error:", error);  
      setAuthError(error.message);  
    }  
  }, []);

  // Reset password  
  const resetPassword = useCallback(async (email) => {  
    setAuthError(null);  
    try {  
      const { error } = await supabase.auth.resetPasswordForEmail(email, {  
        redirectTo: `${window.location.origin}/reset-password`,  
      });  
        
      if (error) throw error;  
      return { error: null };  
    } catch (error) {  
      console.error("Reset password error:", error);  
      setAuthError(error.message);  
      return { error };  
    }  
  }, []);

  // Update password when user has reset token  
  const updatePassword = useCallback(async (newPassword) => {  
    setAuthError(null);  
    try {  
      const { error } = await supabase.auth.updateUser({  
        password: newPassword,  
      });  
        
      if (error) throw error;  
      return { error: null };  
    } catch (error) {  
      console.error("Update password error:", error);  
      setAuthError(error.message);  
      return { error };  
    }  
  }, []);  
    
  // Check if user has admin role  
  const checkAdmin = useCallback(async () => {  
    if (!user) return false;  
      
    try {  
      const { data, error } = await supabase  
        .from('user_roles')  
        .select('*')  
        .eq('user_id', user.id)  
        .eq('role', 'admin')  
        .single();  
        
      if (error || !data) return false;  
      return true;  
    } catch (error) {  
      console.error("Admin check error:", error);  
      return false;  
    }  
  }, [user]);

  // Provide auth context  
  const value = {  
    user,  
    loading,  
    authError,  
    signUp,  
    signIn,  
    signOut,  
    resetPassword,  
    updatePassword,  
    checkAdmin  
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;  
});

export const useAuth = () => useContext(AuthContext);